import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  FormInstance,
  InputNumber,
} from 'antd';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import {
  ContractResponseDto,
  ContractResponseDtoContractStatusEnum,
  ContractResponseDtoTransactionTypeEnum,
  CreateSubmissionPayloadDtoPolicyTypeEnum,
  FilterBrokersFieldEnum,
  FilterBrokersOrderEnum,
  FilterBrokersStatusEnum,
  FilterReinsuredsOrderEnum,
  FilterReinsuredsStatusEnum,
  SubmissionResponseDtoTransactionTypeEnum,
  UpdateContractPayloadDtoPolicyTypeEnum,
} from 'libs/pinnacle-sdk';
import { calculatePaymentWarrantyDate } from 'components/features/binding/contract-detail/transaction-details/form';
import { useWatch } from 'antd/es/form/Form';
import CalendarIcon from 'assets/icons/calendar';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import { userApi, brokerApi, reinsuredApi } from 'services/apis';
import { SortedSelect } from 'components/ui/sorted-select';
import { formatLocaleNumber } from 'utils/format';
import { EQueryKeys } from 'constants/query-keys';
import { MAX_PAGE_SIZE } from 'constants/paginations';
import { ECountries } from 'constants/enums';

interface TransactionDetailsSectionProps {
  form: FormInstance;
  hiddenReferenceNumber: boolean;
  disabled?: boolean;
  contract?: ContractResponseDto;
  submissionTransactionType: SubmissionResponseDtoTransactionTypeEnum;
}

const TransactionDetailsSection: React.FC<TransactionDetailsSectionProps> = ({
  form,
  hiddenReferenceNumber,
  disabled = false,
  contract,
  submissionTransactionType,
}) => {
  const { t } = useTranslation();

  const inceptionDate = useWatch('inceptionDate', form);
  const premiumPaymentWarranty = useWatch('premiumPaymentWarranty', form);
  const declaration = useWatch('isDeclaration', form);
  const mainRiskTerritory = useWatch('mainRiskTerritory', form);

  const isAustralia = useMemo(() => {
    return mainRiskTerritory === ECountries.AUSTRALIA;
  }, [mainRiskTerritory]);

  useEffect(() => {
    if (isAustralia) {
      form.setFieldValue(
        'policyType',
        UpdateContractPayloadDtoPolicyTypeEnum.Reinsurance
      );
    }
  }, [isAustralia, form]);
  const transactionTypeOptions = useMemo(() => {
    const allOptions = Object.values(
      ContractResponseDtoTransactionTypeEnum
    ).map((name) => ({
      label: name,
      value: name,
    }));

    if (
      submissionTransactionType ===
      ContractResponseDtoTransactionTypeEnum.NewBusiness
    ) {
      return allOptions.filter(
        (opt) =>
          opt.value === ContractResponseDtoTransactionTypeEnum.NewBusiness
      );
    }
    return allOptions;
  }, [submissionTransactionType]);

  const policyTypeOptions = useMemo(() => {
    const allOptions = Object.values(
      UpdateContractPayloadDtoPolicyTypeEnum
    ).map((status) => ({
      label: status,
      value: status,
    }));
    if (isAustralia) {
      return allOptions.filter(
        (opt) =>
          opt.value === UpdateContractPayloadDtoPolicyTypeEnum.Reinsurance
      );
    }
    return allOptions;
  }, [isAustralia]);
  // Calculate Payment Warranty Date when Inception Date or Premium Payment Warranty changes
  useEffect(() => {
    if (inceptionDate && premiumPaymentWarranty) {
      const paymentWarrantyDate = calculatePaymentWarrantyDate(
        inceptionDate,
        premiumPaymentWarranty
      );
      form.setFieldValue('paymentWarrantyDate', paymentWarrantyDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inceptionDate, premiumPaymentWarranty]);

  useEffect(() => {
    if (declaration !== undefined && declaration !== null) {
      const newInstallmentsValue = !declaration;
      form.setFieldValue('installments', newInstallmentsValue);
    }
  }, [declaration]);
  return (
    <FormSection
      title={t('contractDetail.transactionDetailsAndClassification')}
      collapsible
    >
      <FormLayoutGroup mdColumns={hiddenReferenceNumber ? 2 : 3}>
        <Form.Item
          label={t('contractDetail.pinnacleReferenceNumber')}
          name="pinnacleReferenceNumber"
          hidden={hiddenReferenceNumber}
        >
          <Input disabled size="large" />
        </Form.Item>

        <Form.Item label={t('contractDetail.bindingDate')} name="bindingDate">
          <DatePicker
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            allowClear={false}
            suffixIcon={<CalendarIcon />}
            disabled={disabled}
          />
        </Form.Item>

        <Form.Item
          label={t('contractDetail.contractStatus')}
          name="contractStatus"
        >
          <SortedSelect
            size="large"
            suffixIcon={<ArrowDownIcon />}
            disabled={disabled}
            options={[
              {
                value: ContractResponseDtoContractStatusEnum.Bound,
                label: ContractResponseDtoContractStatusEnum.Bound,
              },
              {
                value: ContractResponseDtoContractStatusEnum.NotTakenUp,
                label: ContractResponseDtoContractStatusEnum.NotTakenUp,
              },
              {
                value: ContractResponseDtoContractStatusEnum.Cancelled,
                label: t('contract.status.cancelled'),
              },
            ]}
          />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={2}>
        <Form.Item label={t('contractDetail.policyType')} name="policyType">
          <Select
            size="large"
            suffixIcon={<ArrowDownIcon />}
            options={policyTypeOptions}
            disabled={disabled || isAustralia}
          />
        </Form.Item>
        <Form.Item
          label={t('contractDetail.transactionType')}
          name="transactionType"
        >
          <Select
            size="large"
            suffixIcon={<ArrowDownIcon />}
            options={transactionTypeOptions}
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item
          label={t('contractDetail.lineOfBusiness')}
          name="lineOfBusiness"
        >
          <Input disabled size="large" />
        </Form.Item>
        <Form.Item label={t('contractDetail.subClass')} name="subClass">
          <Input disabled size="large" />
        </Form.Item>
        <Form.Item label={t('contractDetail.binder')} name="binder">
          <Input disabled size="large" />
        </Form.Item>
        <Form.Item
          label={t('submission.namedUnderwriter')}
          name="namedUnderwriterId"
        >
          <LazyLoadingSelect
            fetchOptions={async (page, pageSize, searchText) => {
              const { items } = await userApi.filterUsers({
                searchText,
                page,
                pageSize,
                isNamedUnderwriter: true,
              });
              return {
                data: items.map((user) => ({
                  label: user.name || '',
                  value: user.userId,
                })),
              };
            }}
            suffixIcon={<ArrowDownIcon />}
            allowClear
            size="large"
            placeholder={t('submission.selectUnderwriter')}
            queryKey="underwriter-lazy-loading-select"
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item label={t('contractDetail.insured')} name="insured">
          <Input disabled size="large" />
        </Form.Item>
        <Form.Item label={t('contractDetail.reinsured')} name="reinsured">
          <LazyLoadingSelect
            fetchOptions={async (page, _, searchText) => {
              const { items } = await reinsuredApi.filterReinsureds({
                status: FilterReinsuredsStatusEnum.Active,
                page: page,
                pageSize: MAX_PAGE_SIZE,
                field: 'name',
                order: FilterReinsuredsOrderEnum.Asc,
                searchText,
              });

              return {
                data: items.map((ri) => ({
                  label: ri.name || '',
                  value: ri.reinsuredId,
                })),
              };
            }}
            suffixIcon={<ArrowDownIcon />}
            additionalOptions={
              contract?.reinsuredId
                ? [
                    {
                      label: contract?.reinsured?.name as string,
                      value: contract?.reinsuredId,
                    },
                  ]
                : []
            }
            allowClear
            size="large"
            placeholder={t('general.select')}
            queryKey={EQueryKeys.REINSURED_LAZY_LOADING_SELECT}
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item label={t('contractDetail.broker')} name="broker">
          <LazyLoadingSelect
            fetchOptions={async (page, _, searchText) => {
              const { items } = await brokerApi.filterBrokers({
                status: FilterBrokersStatusEnum.Active,
                page: page,
                pageSize: MAX_PAGE_SIZE,
                field: FilterBrokersFieldEnum.Name,
                order: FilterBrokersOrderEnum.Asc,
                searchText,
              });
              return {
                data: items.map((br) => ({
                  label: br.name || '',
                  value: br.brokerId,
                })),
              };
            }}
            additionalOptions={
              contract?.brokerId
                ? [
                    {
                      label: contract?.broker?.name as string,
                      value: contract?.brokerId,
                    },
                  ]
                : []
            }
            suffixIcon={<ArrowDownIcon />}
            allowClear
            size="large"
            placeholder={t('general.select')}
            queryKey={EQueryKeys.BROKER_LAZY_LOADING_SELECT}
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item
          label={t('contractDetail.brokerReference')}
          name="brokerReference"
        >
          <Input size="large" disabled={disabled} />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={3}>
        <Form.Item
          label={t('contractDetail.underwritingYear')}
          name="underwritingYear"
        >
          <Input disabled size="large" />
        </Form.Item>

        <Form.Item
          label={t('contractDetail.inceptionDate')}
          name="inceptionDate"
        >
          <DatePicker
            disabled
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            suffixIcon={<CalendarIcon />}
          />
        </Form.Item>

        <Form.Item label={t('contractDetail.expiryDate')} name="expiryDate">
          <DatePicker
            disabled
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            suffixIcon={<CalendarIcon />}
          />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={2}>
        <Form.Item label={t('contractDetail.declaration')} name="isDeclaration">
          <Select
            size="large"
            suffixIcon={<ArrowDownIcon />}
            options={[
              { value: true, label: t('general.yes') },
              { value: false, label: t('general.no') },
            ]}
            disabled={disabled}
          />
        </Form.Item>

        <Form.Item label={t('contractDetail.installments')} name="installments">
          <Select
            size="large"
            suffixIcon={<ArrowDownIcon />}
            options={[
              { value: true, label: t('general.yes') },
              { value: false, label: t('general.no') },
            ]}
            disabled={disabled || declaration}
          />
        </Form.Item>

        <Form.Item
          label={t('contractDetail.premiumPaymentWarranty')}
          name="premiumPaymentWarranty"
        >
          <InputNumber<number>
            size="large"
            className="w-full"
            precision={0}
            min={1}
            formatter={formatLocaleNumber}
            placeholder={t('general.enter', {
              field: t('contractAdjustment.premiumPaymentWarranty'),
            })}
            disabled={disabled}
          />
        </Form.Item>

        <Form.Item
          label={t('contractDetail.paymentWarrantyDate')}
          name="paymentWarrantyDate"
        >
          <DatePicker
            disabled
            size="large"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            suffixIcon={<CalendarIcon />}
          />
        </Form.Item>
      </FormLayoutGroup>

      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          label={t('contractDetail.principalOccupation')}
          name="principalOccupation"
        >
          <Input disabled size="large" />
        </Form.Item>
        <Form.Item
          label={t('contractDetail.mainRiskTerritory')}
          name="mainRiskTerritory"
        >
          <Input disabled size="large" />
        </Form.Item>
      </FormLayoutGroup>
      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          label={t('contractDetail.oldPinReferenceNumber')}
          name="oldPinReferenceNumber"
        >
          <Input disabled={disabled} size="large" />
        </Form.Item>
      </FormLayoutGroup>
    </FormSection>
  );
};

export default TransactionDetailsSection;
